import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, FileText, Database, RefreshCw, ChevronUp, ChevronDown } from 'lucide-react';
import logoImage from '../images/image.png';
import toast from 'react-hot-toast';
import { grantsAPI } from '../services/api';
import api from '../services/api';
import { Grant } from '../types';

interface Publication {
  id: number;
  title: string;
  authors: string;
  abstract: string;
  doi: string;
  journal: string;
  published_date: string;
  grant_number: string;
  citations?: number;
}

const SavicLabPublications: React.FC = () => {
  const navigate = useNavigate();
  const [grants, setGrants] = useState<Grant[]>([]);
  const [publications, setPublications] = useState<Publication[]>([]);
  const [selectedGrant, setSelectedGrant] = useState<Grant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPublications, setIsLoadingPublications] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  
  // Sorting state
  const [sortField, setSortField] = useState<'grant_number' | 'grant_title' | 'funding_agency' | 'publication_count'>('grant_number');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    loadGrants();
  }, []);

  const loadGrants = async () => {
    try {
      setIsLoading(true);
      // Use batch endpoint to reduce API calls
      const grants = await api.get('/grants/batch');
      setGrants(grants.data);
    } catch (error) {
      console.error('Error loading grants:', error);
      toast.error('Failed to load grants');
      // Set empty array as fallback
      setGrants([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadPublicationsForGrant = async (grant: Grant) => {
    try {
      setIsLoadingPublications(true);
      setSelectedGrant(grant);
      setCurrentPage(1); // Reset to first page when loading new grant
      
      const publications = await grantsAPI.getGrantPublications(grant.id);
      setPublications(publications);
    } catch (error) {
      console.error('Error loading publications:', error);
      toast.error('Failed to load publications');
    } finally {
      setIsLoadingPublications(false);
    }
  };

  const getPubMedUrl = (grantNumber: string) => {
    const encodedGrant = encodeURIComponent(grantNumber);
    return `https://pubmed.ncbi.nlm.nih.gov/?term=%28${encodedGrant}%5BGrant+Number%5D%29`;
  };

  const handleRefreshPublications = async () => {
    if (!selectedGrant) return;
    
    try {
      setIsRefreshing(true);
      const result = await grantsAPI.refreshGrantPublications(selectedGrant.id);
      toast.success(`Found ${result.publications_found} publications, added ${result.new_publications_added} new ones`);
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../services/cache');
      cacheService.clearPattern('/grants/batch');
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      // Reload publications to show updated data
      await loadPublicationsForGrant(selectedGrant);
    } catch (error) {
      console.error('Error refreshing publications:', error);
      toast.error('Failed to refresh publications');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRefreshAllGrants = async () => {
    try {
      setIsRefreshing(true);
      let totalPublications = 0;
      let totalNewPublications = 0;
      let successCount = 0;
      let errorCount = 0;

      toast.loading('Refreshing all grants from PubMed...', { id: 'refresh-all' });

      // Refresh each grant sequentially to avoid overwhelming the API
      for (const grant of grants) {
        try {
          console.log(`Refreshing grant: ${grant.grant_number}`);
          const result = await grantsAPI.refreshGrantPublications(grant.id);
          totalPublications += result.publications_found;
          totalNewPublications += result.new_publications_added;
          successCount++;
          
          // Small delay between requests to be respectful to PubMed API
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (error) {
          console.error(`Error refreshing grant ${grant.grant_number}:`, error);
          errorCount++;
        }
      }

      // Clear cache to ensure fresh data
      const { cacheService } = await import('../services/cache');
      cacheService.clearPattern('/grants/batch');
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      // Reload grants to update publication counts
      await loadGrants();

      // If a grant was selected, reload its publications to show updated data
      if (selectedGrant) {
        await loadPublicationsForGrant(selectedGrant);
      }

      // Show summary toast
      toast.dismiss('refresh-all');
      if (errorCount === 0) {
        toast.success(`Successfully refreshed all ${successCount} grants! Found ${totalPublications} total publications, added ${totalNewPublications} new ones.`);
      } else {
        toast.success(`Refreshed ${successCount} grants successfully, ${errorCount} failed. Found ${totalPublications} total publications, added ${totalNewPublications} new ones.`);
      }
    } catch (error: any) {
      console.error('Error refreshing all grants:', error);
      toast.dismiss('refresh-all');
      toast.error('Failed to refresh grants from PubMed');
    } finally {
      setIsRefreshing(false);
    }
  };


  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSort = (field: 'grant_number' | 'grant_title' | 'funding_agency' | 'publication_count') => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending direction
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedGrants = () => {
    return [...grants].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'grant_number':
          aValue = a.grant_number;
          bValue = b.grant_number;
          break;
        case 'grant_title':
          aValue = a.grant_title;
          bValue = b.grant_title;
          break;
        case 'funding_agency':
          aValue = a.funding_agency || '';
          bValue = b.funding_agency || '';
          break;
        case 'publication_count':
          aValue = a.publication_count || 0;
          bValue = b.publication_count || 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortDirection === 'asc' 
          ? (aValue as number) - (bValue as number)
          : (bValue as number) - (aValue as number);
      }
    });
  };

  const formatAuthors = (authors: string) => {
    if (!authors) return 'Unknown authors';
    
    // Split by comma and get the first author
    const authorList = authors.split(',').map(author => author.trim());
    const firstAuthor = authorList[0];
    
    // If there's only one author, return as is
    if (authorList.length === 1) {
      return firstAuthor;
    }
    
    // Return first author followed by "et al."
    return `${firstAuthor} et al.`;
  };

  // Pagination logic
  const totalPages = Math.ceil(publications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPublications = publications.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of publications table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <img 
            src={logoImage} 
            alt="Publication Tracker Logo" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading grants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm text-green-100 mb-6">
            <button 
              onClick={() => navigate('/publications')} 
              className="flex items-center space-x-1 text-green-100 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Dashboard</span>
            </button>
          </div>
          
          <div className="text-center">
            <img 
              src={logoImage} 
              alt="Publication Tracker Logo" 
              className="h-20 w-auto mx-auto mb-6"
            />
            <h1 className="text-4xl font-bold mb-4">Savic Lab Publications</h1>
            <p className="text-xl text-green-100 max-w-3xl mx-auto">
              Track publications by grant number with PubMed integration
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!selectedGrant ? (
          /* Grants Table View */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                    <Database className="h-6 w-6 mr-2 text-green-600" />
                    Grant-Funded Publications
                  </h2>
                  <p className="text-gray-600 mt-2">Click on a grant to view its associated publications</p>
                </div>
                <button
                  onClick={handleRefreshAllGrants}
                  disabled={isRefreshing}
                  className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  <span>{isRefreshing ? 'Refreshing...' : 'Refresh All from PubMed'}</span>
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('grant_number')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Grant Number</span>
                        {sortField === 'grant_number' && (
                          sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('grant_title')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Grant Title</span>
                        {sortField === 'grant_title' && (
                          sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('funding_agency')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Funding Agency</span>
                        {sortField === 'funding_agency' && (
                          sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('publication_count')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Publications</span>
                        {sortField === 'publication_count' && (
                          sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(grants) && grants.length > 0 ? getSortedGrants().map((grant) => (
                    <tr 
                      key={grant.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => loadPublicationsForGrant(grant)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{grant.grant_number}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-md">{grant.grant_title}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{grant.funding_agency || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FileText className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-sm text-gray-900">{grant.publication_count || 0}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(getPubMedUrl(grant.grant_number), '_blank');
                            }}
                            className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 transition-colors"
                            title="View on PubMed"
                          >
                            <ExternalLink className="h-4 w-4" />
                            <span className="text-xs">PubMed</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        <div className="flex flex-col items-center">
                          <Database className="h-12 w-12 text-gray-300 mb-4" />
                          <p className="text-lg font-medium">No grants found</p>
                          <p className="text-sm">Unable to load grants data</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Publications for Selected Grant */
          <div className="space-y-6">
            {/* Grant Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setSelectedGrant(null)}
                  className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back to Grants</span>
                </button>
                <button
                  onClick={() => window.open(getPubMedUrl(selectedGrant.grant_number), '_blank')}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>View on PubMed</span>
                </button>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedGrant.grant_number}</h2>
              <h3 className="text-lg text-gray-700 mb-4">{selectedGrant.grant_title}</h3>
              <p className="text-gray-600 mb-4">{selectedGrant.description || 'No description available'}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <span><strong>Agency:</strong> {selectedGrant.funding_agency || 'N/A'}</span>
              </div>
            </div>

            {/* Publications Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-green-600" />
                    Publications ({publications.length})
                  </h3>
                  <button
                    onClick={handleRefreshPublications}
                    disabled={isRefreshing}
                    className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    <span>{isRefreshing ? 'Refreshing...' : 'Refresh from PubMed'}</span>
                  </button>
                </div>
              </div>

              {isLoadingPublications ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading publications...</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Authors
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Published
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Citations
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          DOI
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentPublications.map((pub) => (
                        <tr key={pub.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 max-w-md">{pub.title}</div>
                            <div className="text-xs text-gray-500 mt-1 line-clamp-2">{pub.abstract}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs">{formatAuthors(pub.authors)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{pub.published_date ? formatDate(pub.published_date) : 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {pub.citations !== undefined ? pub.citations.toLocaleString() : 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {pub.doi ? (
                              <a
                                href={`https://doi.org/${pub.doi}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-700 text-sm"
                              >
                                https://doi.org/{pub.doi}
                              </a>
                            ) : (
                              <span className="text-gray-400 text-sm">N/A</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {publications.length > itemsPerPage && (
                  <div className="flex items-center justify-between mt-6 px-6 py-3 bg-white border-t border-gray-200">
                    <div className="flex items-center text-sm text-gray-700">
                      <span>
                        Showing {startIndex + 1} to {Math.min(endIndex, publications.length)} of {publications.length} publications
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      {/* Page numbers */}
                      <div className="flex space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum: number;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`px-3 py-1 text-sm font-medium rounded-md ${
                                currentPage === pageNum
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SavicLabPublications;
